import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import useFetchCollection from '@/Framework/State/useFetchCollection';
import { useDIContext } from '@/Framework/DI/DIContext';
import PortfolioMonitorRepository from '@/finsight/infrastructure/repository/PortfolioMonitorRepository';
import { usePortfoliosContext } from '@/finsight/application/PortfolioMonitor/PortfoliosContext';
import {
  IPositionItem, IPositionsResponse,
} from '@/finsight/domain/vo/PortfolioMonitor/IPositionsCollection';
import { useRouter } from '@/Framework/Router';
import { addQueryParams, cleanQueryParams } from '@/Framework/Router/nextRouter';
import useMySelection from '@/finsight/ui/common/hooks/useMySelection';
import { IDatetime } from '@/Framework/DateTime/Datetime';
import { AlertManager } from '@dealroadshow/uikit/core/components/Alert';
import { getErrorMessage } from '@/Framework/Message/Mapper/getMessage';
import useCancelChangesModal from '@/dataroom/ui/common/Questions/CancelChangesModal/useCancelChangesModal';

export const usePositions = () => {
  const portfolioMonitorRepository = useDIContext()
      .container
      .get<PortfolioMonitorRepository>(PortfolioMonitorRepository);
  const [lastUpdated, setLastUpdated] = useState<IDatetime>();
  const { selectedPortfolioList, selectedPortfolio } = usePortfoliosContext();
  const { push, query } = useRouter();
  const { q = '' }: { q?: string } = query;
  const [search, setSearch] = useState<string>(q);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchPositions = useCallback(async (payload) => {
    const response: IPositionsResponse = await portfolioMonitorRepository.getPositionsByList(payload);
    setLastUpdated(response.lastUpdatedAt);
    return response.positions;
  }, [selectedPortfolioList]);

  const positionList = useFetchCollection<IPositionItem>(
      !selectedPortfolioList?.id ? null : fetchPositions,
      {
        listId: selectedPortfolioList?.id || null,
      },
      {
        sortBy: 'name',
        sortOrder: 'asc',
        debouncedSearchString: search,
      },
  );

  useEffect(() => positionList.reset, []);

  const onSearch = (value:string) => {
    setSearch(value);
    positionList.debouncedSearch(value);
  };

  const onSetSearchUrlParam = (search) => {
    const newQuery = addQueryParams(query, { q: search });

    push({ query: cleanQueryParams(newQuery) }, undefined, { shallow: true });
  };

  const selection = useMySelection((item: string) => item);

  useEffect(() => {
    if (selectedPortfolio?.id) {
      selection.reset();
    }
  }, [selectedPortfolio?.id]);

  const confirmDeletePositionsModal = useCancelChangesModal();

  const confirmDeletePositions = async () => {
    setIsLoading(true);
    confirmDeletePositionsModal.hideCanceledModal();
    await bulkDeletePositions(selection.collection);
    await positionList.getCollection();
    selection.reset();
    setIsLoading(false);
  };

  const bulkDeletePositions = async (cusips: string[]) => {
    const payload = {
      portfolioId: selectedPortfolio.id,
      listId: selectedPortfolioList.id,
      cusips,
    };
    try {
      await portfolioMonitorRepository.deletePositions(payload);

      AlertManager.success(`${ cusips.length } position${ cusips.length > 1 ? 's have' : ' has' } been deleted.`);
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
    }
  };

  return {
    positionList,
    lastUpdated,
    search,
    onSearch,
    onSetSearchUrlParam,
    selection,
    confirmDeletePositionsModal,
    confirmDeletePositions,
    isLoading,
  };
};

type PositionsContextType = ReturnType<typeof usePositions>;

export const PositionsContext = createContext<PositionsContextType>(null);

export const usePositionsContext = () => {
  const context = useContext(PositionsContext);
  if (!context) {
    throw new Error('usePositionsContext must be used within the PositionsContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const PositionsContextProvider = ({ children }: IProps) => (
  <PositionsContext.Provider value={ usePositions() }>
    { children }
  </PositionsContext.Provider>
);

export default PositionsContextProvider;
